import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { dokumenteApi } from '../api';

const emptyFilters = {
  q: '',
  vertrag_id: '',
  anbieter: '',
  kategorie: '',
  dokumenttyp: '',
  ocr: '',
  ki: '',
  favorit: '',
  tag: '',
  von: '',
  bis: '',
};

const BG_PANEL = '#1e293b';
const BORDER = '#334155';
const TEXT_BODY = '#94a3b8';
const EMPTY_STATE_TEXT = { vertrage: 'Keine Verträge vorhanden', dokumente: 'Keine Dokumente vorhanden', kundigungen: 'Keine Kündigungen vorhanden' };

export default function DokumentePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');
  const [ocrMessage, setOcrMessage] = useState('');
  const navigate = useNavigate();
  const selectedId = searchParams.get('dokument');

  useEffect(() => {
    loadDocuments(filters);
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    setDetailLoading(true);
    setOcrMessage('');
    dokumenteApi.detail(selectedId)
      .then(res => setDetail(res.data))
      .catch(() => setError('Dokumentdetails konnten nicht geladen werden.'))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  const options = data?.filter || {};
  const documents = data?.dokumente || [];
  const overview = data?.ueberblick || {};

  function loadDocuments(nextFilters) {
    setLoading(true);
    setError('');
    const params = Object.fromEntries(Object.entries(nextFilters).filter(([, value]) => value !== ''));
    dokumenteApi.list(params)
      .then(res => setData(res.data))
      .catch(() => setError('Dokumentencenter konnte nicht geladen werden.'))
      .finally(() => setLoading(false));
  }

  function updateFilter(name, value) {
    const next = { ...filters, [name]: value };
    setFilters(next);
    loadDocuments(next);
  }

  function resetFilters() {
    setFilters(emptyFilters);
    loadDocuments(emptyFilters);
  }

  function openDocument(id) {
    setSearchParams({ dokument: String(id) });
  }

  function closeDetail() {
    setSearchParams({});
  }

  function restartOcr() {
    if (!detail?.id) return;
    setOcrMessage('');
    dokumenteApi.ocrNeustart(detail.id)
      .then(res => {
        setOcrMessage(res.data.hinweis || 'OCR-Neustart wurde vorbereitet.');
        setTimeout(() => {
          dokumenteApi.detail(detail.id).then(updated => setDetail(updated.data)).catch(() => {});
          loadDocuments(filters);
        }, 3000);
      })
      .catch(() => setOcrMessage('OCR-Neustart konnte nicht vorbereitet werden.'));
  }

  function restartKi() {
    if (!detail?.id) return;
    setOcrMessage('');
    dokumenteApi.kiNeustart(detail.id)
      .then(res => {
        setOcrMessage(res.data.hinweis || 'KI-Neustart wurde vorbereitet.');
        setTimeout(() => {
          dokumenteApi.detail(detail.id).then(updated => setDetail(updated.data)).catch(() => {});
          loadDocuments(filters);
        }, 3000);
      })
      .catch(() => setOcrMessage('KI-Neustart konnte nicht vorbereitet werden.'));
  }

  const groupedDocuments = useMemo(() => ({
    alle: documents,
    unverarbeitet: documents.filter(doc => !doc.verarbeitungsstatus || doc.verarbeitungsstatus === 'ausstehend'),
    wirdVerarbeitet: documents.filter(doc => doc.verarbeitungsstatus === 'wird_verarbeitet'),
    fehlgeschlagen: documents.filter(doc => doc.verarbeitungsstatus === 'fehlgeschlagen'),
    nichtZugeordnet: documents.filter(doc => !doc.vertrag_id || Number(doc.vertrag_id) === 0),
    ocrOffen: documents.filter(doc => !doc.ocr_vorhanden),
    kiOffen: documents.filter(doc => !doc.ki_vorhanden),
    favoriten: documents.filter(doc => doc.favorit),
  }), [documents]);

  if (loading && !data) return <StateScreen text="Dokumentencenter wird geladen..." />;
  if (error && !data) return <StateScreen title="Nicht verfügbar" text={error} />;
  if (documents.length === 0) {
    return (
      <div style={{ backgroundColor: BG_PANEL, border: `1px solid ${BORDER}`, color: TEXT_BODY, padding: '2rem', textAlign: 'center', margin: '2rem' }}>
        {EMPTY_STATE_TEXT.dokumente}
      </div>
    );
  }

  return (
    <div style={page}>
      <section style={hero}>
        <div>
          <div style={eyebrow}>Dokumentencenter</div>
          <h1 style={title}>Dokumente</h1>
          <p style={subtitle}>Zentrale Verwaltung für Vertragsunterlagen, Rechnungen, OCR-Texte, KI-Vorbereitung, Versionen und Tags.</p>
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button className="btn-secondary" onClick={() => navigate('/assistant?message=Welche Dokumente sollte ich prüfen?')}>Dokumente mit KI prüfen</button>
          <UploadButton onUpload={() => loadDocuments(filters)} />
          <button className="btn-primary" onClick={() => navigate('/vertraege')}>Zu Verträgen</button>
        </div>
      </section>

      <section style={kpiGrid}>
        <Metric label="Dokumente" value={overview.gesamt || 0} icon="📄" tone="#38bdf8" />
        <Metric label="OCR offen" value={overview.ocr_offen || 0} icon="🔎" tone="#fbbf24" />
        <Metric label="KI offen" value={overview.ki_offen || 0} icon="✨" tone="#a78bfa" />
        <Metric label="Wichtig" value={overview.favoriten || 0} icon="★" tone="#34d399" />
        <Metric label="Ohne Vertrag" value={overview.ohne_vertrag || 0} icon="⚠️" tone="#f87171" />
        <Metric label="Neue KI-Analysen" value={data?.intelligence?.kpis?.neue_ki_analysen || 0} icon="🤖" tone="#22d3ee" />
        <Metric label="Risikodokumente" value={data?.intelligence?.kpis?.risikodokumente || 0} icon="⚠️" tone="#fb7185" />
        <Metric label="Dokumente mit Fehlern" value={data?.intelligence?.kpis?.dokumente_mit_fehler || 0} icon="⛔" tone="#f97316" />
      </section>

      <section style={filterPanel}>
        <input
          value={filters.q}
          onChange={e => updateFilter('q', e.target.value)}
          placeholder="Dokumente, OCR, Tags, Vertrag oder Anbieter suchen"
          style={searchInput}
        />
        <div style={filterGrid}>
          <Select label="Vertrag" value={filters.vertrag_id} onChange={value => updateFilter('vertrag_id', value)}
            options={(options.vertraege || []).map(item => ({ value: item.id, label: item.titel }))} />
          <Select label="Anbieter" value={filters.anbieter} onChange={value => updateFilter('anbieter', value)}
            options={(options.anbieter || []).map(item => ({ value: item, label: item }))} />
          <Select label="Kategorie" value={filters.kategorie} onChange={value => updateFilter('kategorie', value)}
            options={(options.kategorien || []).map(item => ({ value: item, label: item }))} />
          <Select label="Typ" value={filters.dokumenttyp} onChange={value => updateFilter('dokumenttyp', value)}
            options={(options.dokumenttypen || []).map(item => ({ value: item, label: item }))} />
          <Select label="OCR" value={filters.ocr} onChange={value => updateFilter('ocr', value)}
            options={[{ value: 'vorhanden', label: 'vorhanden' }, { value: 'fehlt', label: 'fehlt' }]} />
          <Select label="KI" value={filters.ki} onChange={value => updateFilter('ki', value)}
            options={[{ value: 'vorhanden', label: 'vorhanden' }, { value: 'fehlt', label: 'fehlt' }]} />
          <Select label="Favoriten" value={filters.favorit} onChange={value => updateFilter('favorit', value)}
            options={[{ value: 'true', label: 'nur wichtige' }]} />
          <Select label="Tag" value={filters.tag} onChange={value => updateFilter('tag', value)}
            options={(options.tags || []).map(item => ({ value: item, label: item }))} />
          <Input label="Von" type="date" value={filters.von} onChange={value => updateFilter('von', value)} />
          <Input label="Bis" type="date" value={filters.bis} onChange={value => updateFilter('bis', value)} />
        </div>
        <div style={filterFooter}>
          <span style={muted}>{overview.gefiltert ?? documents.length} Treffer</span>
          <button className="btn-secondary" onClick={resetFilters}>Filter zurücksetzen</button>
        </div>
      </section>

      {(groupedDocuments.wirdVerarbeitet.length > 0 || groupedDocuments.fehlgeschlagen.length > 0 || groupedDocuments.nichtZugeordnet.length > 0) && (
        <section style={processingBar}>
          {groupedDocuments.wirdVerarbeitet.length > 0 && (
            <div style={processingBadge('#6366f1')}>
              <span style={{ fontSize: 18 }}>⚙️</span>
              <span>
                <strong>{groupedDocuments.wirdVerarbeitet.length}</strong>{' '}
                {groupedDocuments.wirdVerarbeitet.length === 1 ? 'Dokument wird' : 'Dokumente werden'} gerade analysiert
              </span>
            </div>
          )}
          {groupedDocuments.fehlgeschlagen.length > 0 && (
            <div style={processingBadge('#ef4444')}>
              <span style={{ fontSize: 18 }}>⛔</span>
              <span>
                <strong>{groupedDocuments.fehlgeschlagen.length}</strong>{' '}
                {groupedDocuments.fehlgeschlagen.length === 1 ? 'Analyse fehlgeschlagen' : 'Analysen fehlgeschlagen'}
              </span>
            </div>
          )}
          {groupedDocuments.nichtZugeordnet.length > 0 && (
            <div style={processingBadge('#f97316')}>
              <span style={{ fontSize: 18 }}>📎</span>
              <span>
                <strong>{groupedDocuments.nichtZugeordnet.length}</strong>{' '}
                {groupedDocuments.nichtZugeordnet.length === 1 ? 'Dokument' : 'Dokumente'} ohne Vertragszuordnung
              </span>
            </div>
          )}
        </section>
      )}

      <section style={sectionGrid}>
        <DocumentSection title="Alle Dokumente" documents={groupedDocuments.alle} query={filters.q} onOpen={openDocument} />
        {groupedDocuments.wirdVerarbeitet.length > 0 && (
          <DocumentSection title="In Verarbeitung" documents={groupedDocuments.wirdVerarbeitet} query={filters.q} onOpen={openDocument} compact />
        )}
        {groupedDocuments.fehlgeschlagen.length > 0 && (
          <DocumentSection title="Analyse fehlgeschlagen" documents={groupedDocuments.fehlgeschlagen} query={filters.q} onOpen={openDocument} compact />
        )}
        {groupedDocuments.nichtZugeordnet.length > 0 && (
          <DocumentSection title="Ohne Vertrag" documents={groupedDocuments.nichtZugeordnet} query={filters.q} onOpen={openDocument} compact />
        )}
        <DocumentSection title="OCR offen" documents={groupedDocuments.ocrOffen} query={filters.q} onOpen={openDocument} compact />
        <DocumentSection title="KI offen" documents={groupedDocuments.kiOffen} query={filters.q} onOpen={openDocument} compact />
        <DocumentSection title="Wichtige Dokumente" documents={groupedDocuments.favoriten} query={filters.q} onOpen={openDocument} compact />
      </section>

      {selectedId && (
        <DetailPanel
          detail={detail}
          loading={detailLoading}
          onClose={closeDetail}
          onRestartOcr={restartOcr}
          onRestartKi={restartKi}
          ocrMessage={ocrMessage}
        />
      )}
    </div>
  );
}

function Metric({ label, value, icon, tone }) {
  return (
    <div style={{ ...metricCard, borderColor: `${tone}55` }}>
      <span style={{ fontSize: 25 }}>{icon}</span>
      <strong style={{ color: tone, fontSize: 24 }}>{value}</strong>
      <span style={muted}>{label}</span>
    </div>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      <select value={value} onChange={e => onChange(e.target.value)} style={field}>
        <option value="">Alle</option>
        {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

function Input({ label, type, value, onChange }) {
  return (
    <label style={fieldLabel}>
      <span>{label}</span>
      <input type={type} value={value} onChange={e => onChange(e.target.value)} style={field} />
    </label>
  );
}

function DocumentSection({ title, documents, query, onOpen, compact }) {
  return (
    <section style={panel}>
      <div style={sectionHeader}>
        <h2 style={sectionTitle}>{title}</h2>
        <span style={countBadge}>{documents.length}</span>
      </div>
      {documents.length === 0 ? (
        <EmptyState text="Keine Dokumente vorhanden." />
      ) : (
        <div style={compact ? compactGrid : documentGrid}>
          {documents.slice(0, compact ? 6 : undefined).map(doc => (
            <DocumentCard key={doc.id} doc={doc} query={query} onOpen={() => onOpen(doc.id)} />
          ))}
        </div>
      )}
    </section>
  );
}

function UploadButton({ onUpload }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState('');

  async function handleFile(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setMsg('');
    const fd = new FormData();
    fd.append('datei', file);
    try {
      await dokumenteApi.uploadFrei(fd);
      setMsg('Dokument hochgeladen – Analyse läuft');
      setTimeout(() => { setMsg(''); onUpload(); }, 2500);
    } catch (err) {
      setMsg(err.response?.data?.error || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <span>
      <input ref={inputRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.webp,.txt" onChange={handleFile} style={{ display: 'none' }} />
      <button className="btn-secondary" onClick={() => inputRef.current?.click()} disabled={uploading}>
        {uploading ? 'Lädt…' : '+ Dokument hochladen'}
      </button>
      {msg && <span style={{ marginLeft: 10, color: '#38bdf8', fontSize: 12 }}>{msg}</span>}
    </span>
  );
}

function DocumentCard({ doc, query, onOpen }) {
  const navigate = useNavigate();
  return (
    <button onClick={onOpen} style={docCard}>
      <div style={docTop}>
        <span style={docIcon(doc.dokumenttyp)}>{doc.dokumenttyp === 'PDF' ? 'PDF' : doc.dokumenttyp === 'Bild' ? 'IMG' : 'DOC'}</span>
        <span style={statusPill(doc.status_tone)}>{doc.status_label}</span>
      </div>
      <div style={docTitle}>{highlight(doc.originalname || doc.dateiname, query)}</div>
      <div style={docMeta}>{highlight(doc.vertrag, query)} · {highlight(doc.anbieter || 'Ohne Anbieter', query)}</div>
      <div style={docInfoGrid}>
        <Info label="Kategorie" value={doc.kategorie} />
        <Info label="Upload" value={formatDate(doc.hochgeladen_am)} />
        <Info label="Größe" value={doc.groesse_label} />
        <Info label="Version" value={`v${doc.version}`} />
      </div>
      <div style={statusRow}>
        <span style={queueBadge(doc.ocr_status)}>{queueLabel(doc.ocr_status, 'OCR')}</span>
        <span style={queueBadge(doc.ki_status)}>{queueLabel(doc.ki_status, 'KI')}</span>
        {doc.verarbeitungsstatus && doc.verarbeitungsstatus !== 'ausstehend' && (
          <span style={pipelineBadge(doc.verarbeitungsstatus)}>
            {doc.verarbeitungsstatus === 'wird_verarbeitet' ? '⚙️ Analyse' :
             doc.verarbeitungsstatus === 'abgeschlossen' ? '✓ Analysiert' :
             doc.verarbeitungsstatus === 'fehlgeschlagen' ? '⛔ Fehler' : doc.verarbeitungsstatus}
          </span>
        )}
        {doc.klassifikation && (
          <span style={{ ...tagPill, color: '#a5b4fc', borderColor: '#6366f144' }}>{doc.klassifikation}</span>
        )}
      </div>
      <div style={tagRow}>
        {doc.favorit && <span style={favorite}>★ Wichtig</span>}
        {doc.tags.slice(0, 5).map(tag => <span key={tag} style={tagPill}>{highlight(tag, query)}</span>)}
      </div>
      <div style={{ marginTop: 6 }}>
        <span
          role="button"
          tabIndex={0}
          onClick={e => { e.stopPropagation(); navigate(`/dokumente/${doc.id}`); }}
          onKeyDown={e => e.key === 'Enter' && navigate(`/dokumente/${doc.id}`)}
          style={{ color: '#38bdf8', fontSize: 11, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline' }}
        >
          Analyse öffnen →
        </span>
      </div>
    </button>
  );
}

function DetailPanel({ detail, loading, onClose, onRestartOcr, onRestartKi, ocrMessage }) {
  return (
    <div style={overlay}>
      <aside style={detailPanel}>
        <div style={detailHeader}>
          <div>
            <div style={eyebrow}>Dokumentdetails</div>
            <h2 style={{ ...sectionTitle, marginTop: 4 }}>{detail?.originalname || 'Dokument'}</h2>
          </div>
          <button onClick={onClose} className="btn-secondary" style={{width:36, height:36, fontSize:22}}>×</button>
        </div>

        {loading || !detail ? (
          <StateScreen text="Details werden geladen..." compact />
        ) : (
          <div style={{ display: 'grid', gap: 14 }}>
            <Preview detail={detail} />
            <DetailSection title="Übersicht">
              <div style={detailGrid}>
                <Info label="Originalname" value={detail.uebersicht.originalname} />
                <Info label="Vertrag" value={detail.uebersicht.vertrag} />
                <Info label="Anbieter" value={detail.anbieter || 'Ohne Anbieter'} />
                <Info label="Kategorie" value={detail.uebersicht.kategorie} />
                <Info label="Dokumenttyp" value={detail.uebersicht.dokumenttyp} />
                <Info label="Uploaddatum" value={formatDate(detail.uebersicht.hochgeladen_am)} />
                <Info label="Größe" value={detail.uebersicht.groesse_label} />
                <Info label="Status" value={detail.status_label} />
              </div>
            </DetailSection>

            <DetailSection title="OCR">
              <div style={detailActions}>
                <span style={statusPill(detail.ocr.vorhanden ? 'success' : 'warning')}>
                  {detail.ocr.vorhanden ? 'OCR vorhanden' : 'OCR fehlt'}
                </span>
                <button className="btn-secondary" onClick={onRestartOcr}>OCR erneut starten</button>
              </div>
              <div style={detailGrid}>
                {[
                  ['Status', detail.ocr.queue?.[0]?.status || detail.ocr.status],
                  ['Gestartet', detail.ocr.queue?.[0]?.started_am ? formatDate(detail.ocr.queue[0].started_am) : '–'],
                  ['Beendet', detail.ocr.queue?.[0]?.finished_am ? formatDate(detail.ocr.queue[0].finished_am) : '–'],
                ].map(([label, value]) => <Info key={label} label={label} value={value} />)}
              </div>
              {ocrMessage && <p style={notice}>{ocrMessage}</p>}
              {detail.ocr.text ? <pre style={ocrBox}>{detail.ocr.text}</pre> : <EmptyState text="Für dieses Dokument liegt noch kein OCR-Text vor." />}
            </DetailSection>

            <DetailSection title="KI">
              <div style={detailActions}>
                <span style={statusPill(detail.ki.vorhanden ? 'success' : 'warning')}>
                  {detail.ki.vorhanden ? 'KI vorhanden' : 'KI offen'}
                </span>
                <button className="btn-secondary" onClick={() => window.location.href = `/assistant?message=${encodeURIComponent(`Erkläre das Dokument ${detail.originalname || detail.dateiname}`)}`}>Dokument erklären</button>
                <button className="btn-secondary" onClick={onRestartKi}>KI neu starten</button>
              </div>
              <div style={detailGrid}>
                <Info label="Modell" value={detail.ki.modell || 'rule-based'} />
                <Info label="Version" value={detail.ki.version || '1.0'} />
                <Info label="Vertrauen" value={detail.ki.vertrauensscore != null ? `${detail.ki.vertrauensscore}%` : '–'} />
                <Info label="Analysiert am" value={detail.ki.analysezeitpunkt ? formatDate(detail.ki.analysezeitpunkt) : '–'} />
                <Info label="Dokumenttyp" value={detail.ki.dokumenttyp || detail.uebersicht.dokumenttyp} />
                <Info label="Vertragstyp" value={detail.ki.vertragstyp || '–'} />
              </div>
              {detail.ki.vorhanden ? (
                <div style={{ display: 'grid', gap: 12 }}>
                  <EmptyState text={detail.ki.zusammenfassung || 'Keine Zusammenfassung vorhanden.'} />
                  <div style={tagRow}>
                    {(detail.ki.schlagwoerter || []).map(item => (
                      <span key={item} style={tagPill}>{item}</span>
                    ))}
                  </div>
                  <div style={detailGrid}>
                    <Info label="Anbieter" value={detail.ki.extrahierte_felder?.anbieter || detail.anbieter || '–'} />
                    <Info label="Vertragsnummer" value={detail.ki.extrahierte_felder?.vertragsnummer || '–'} />
                    <Info label="Kundennummer" value={detail.ki.extrahierte_felder?.kundennummer || '–'} />
                    <Info label="Preis" value={detail.ki.extrahierte_felder?.preis != null ? `${detail.ki.extrahierte_felder.preis} €` : '–'} />
                    <Info label="Vertragsbeginn" value={detail.ki.extrahierte_felder?.vertragsbeginn || '–'} />
                    <Info label="Vertragsende" value={detail.ki.extrahierte_felder?.vertragsende || '–'} />
                    <Info label="Kündigungsfrist" value={detail.ki.extrahierte_felder?.kuendigungsfrist || '–'} />
                    <Info label="Mindestlaufzeit" value={detail.ki.extrahierte_felder?.mindestlaufzeit || '–'} />
                  </div>
                  <div style={warningGrid}>
                    {(detail.ki.risiken || []).length ? detail.ki.risiken.map(risk => (
                      <div key={risk} style={warningCard}>{risk}</div>
                    )) : <EmptyState text="Keine Risiken erkannt." />}
                  </div>
                  <div style={detailGrid}>
                    <Info label="Kündigungsklauseln" value={(detail.ki.kuendigungsklauseln || []).join(', ') || '–'} />
                    <Info label="Laufzeiten" value={(detail.ki.laufzeiten || []).join(', ') || '–'} />
                    <Info label="Fristen" value={(detail.ki.fristen || []).join(', ') || '–'} />
                  </div>
                </div>
              ) : (
                <>
                  <EmptyState text={detail.ki.hinweis} />
                  <div style={tagRow}>
                    {['Zusammenfassung', 'Schlagwörter', 'Risiken', 'Kündigungsklauseln', 'Vertragstyp'].map(item => (
                      <span key={item} style={tagPill}>{item}</span>
                    ))}
                  </div>
                </>
              )}
            </DetailSection>

            <DetailSection title="Analysehistorie">
              {(detail.historie || []).filter(entry => entry.typ === 'ocr' || entry.typ === 'ki').length ? (
                (detail.historie || []).filter(entry => entry.typ === 'ocr' || entry.typ === 'ki').map((entry, index) => (
                  <Timeline key={`${entry.typ}-${index}`} title={entry.titel} text={entry.ziel || entry.typ} date={entry.datum} />
                ))
              ) : (
                <EmptyState text="Noch keine Analysehistorie vorhanden." />
              )}
            </DetailSection>

            <DetailSection title="Versionen">
              {detail.versionen.map(version => (
                <Timeline key={version.version} title={`Version ${version.version}`} text={`Status: ${version.status}${version.ersetzt_durch ? ` · ersetzt durch ${version.ersetzt_durch}` : ''}`} date={version.erstellt_am} />
              ))}
            </DetailSection>

            <DetailSection title="Historie">
              {detail.historie.map((entry, index) => (
                <Timeline key={`${entry.typ}-${index}`} title={entry.titel} text={entry.ziel || entry.typ} date={entry.datum} />
              ))}
            </DetailSection>

            <DetailSection title="Metadaten">
              <div style={detailGrid}>
                <Info label="ID" value={detail.metadaten.id} />
                <Info label="Vertrag-ID" value={detail.metadaten.vertrag_id} />
                <Info label="MIME-Type" value={detail.metadaten.mimetype || 'unbekannt'} />
                <Info label="Favorit" value={detail.metadaten.favorit ? 'Ja' : 'Nein'} />
                <Info label="Tags" value={detail.metadaten.tags.join(', ') || 'Keine'} />
              </div>
            </DetailSection>
          </div>
        )}
      </aside>
    </div>
  );
}

function Preview({ detail }) {
  const src = dokumenteApi.previewUrl(detail.preview_url);
  const type = String(detail.dateityp || '').toLowerCase();
  return (
    <DetailSection title="Vorschau">
      {type.includes('pdf') ? (
        <iframe title={detail.originalname} src={src} style={previewFrame} />
      ) : type.startsWith('image/') ? (
        <img src={src} alt={detail.originalname} style={previewImage} />
      ) : (
        <div style={placeholder}>Für diesen Dateityp ist keine direkte Vorschau verfügbar.</div>
      )}
    </DetailSection>
  );
}

function DetailSection({ title, children }) {
  return (
    <section style={detailSection}>
      <h3 style={smallTitle}>{title}</h3>
      {children}
    </section>
  );
}

function Info({ label, value }) {
  return (
    <span style={infoBox}>
      <span style={infoLabel}>{label}</span>
      <strong style={infoValue}>{value || 'Keine Angabe'}</strong>
    </span>
  );
}

function Timeline({ title, text, date }) {
  return (
    <div style={timeline}>
      <span style={timelineDot} />
      <span>
        <strong style={{ color: '#f8fafc', display: 'block' }}>{title}</strong>
        <span style={muted}>{text} · {formatDate(date)}</span>
      </span>
    </div>
  );
}

function EmptyState({ text }) {
  return <div style={emptyState}>{text}</div>;
}

function StateScreen({ title = 'Bitte warten', text, compact }) {
  return (
    <div style={{ ...stateScreen, minHeight: compact ? 180 : '60vh' }}>
      <h2 style={sectionTitle}>{title}</h2>
      <p style={muted}>{text}</p>
    </div>
  );
}

function highlight(value, query) {
  const text = String(value || '');
  const needle = String(query || '').trim();
  if (needle.length < 2) return text;
  const index = text.toLowerCase().indexOf(needle.toLowerCase());
  if (index === -1) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark style={mark}>{text.slice(index, index + needle.length)}</mark>
      {text.slice(index + needle.length)}
    </>
  );
}

function formatDate(value) {
  if (!value) return 'Keine Angabe';
  return new Date(String(value).replace(' ', 'T')).toLocaleDateString('de-DE');
}

function docIcon(type) {
  return {
    ...docIconBase,
    background: type === 'PDF' ? '#7f1d1d' : type === 'Bild' ? '#164e63' : '#312e81',
    color: type === 'PDF' ? '#fecaca' : type === 'Bild' ? '#a5f3fc' : '#c4b5fd',
  };
}

function statusPill(tone) {
  const color = tone === 'success' ? '#34d399' : tone === 'warning' ? '#fbbf24' : '#38bdf8';
  return { ...pill, color, borderColor: `${color}66`, background: `${color}14` };
}

const page = { padding: 18, maxWidth: 1240, margin: '0 auto', display: 'grid', gap: 16 };
const hero = { display: 'flex', justifyContent: 'space-between', gap: 18, alignItems: 'center', background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 20, flexWrap: 'wrap' };
const eyebrow = { color: '#38bdf8', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0 };
const title = { color: '#f8fafc', fontSize: 32, lineHeight: 1.1, margin: '4px 0 8px' };
const subtitle = { color: '#cbd5e1', margin: 0, maxWidth: 720, fontSize: 15 };

const kpiGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 };
const metricCard = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 15, display: 'grid', gap: 7 };
const filterPanel = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 14, display: 'grid', gap: 12 };
const filterGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 10 };
const filterFooter = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' };
const searchInput = { width: '100%', boxSizing: 'border-box', background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '12px 13px', fontSize: 15 };
const fieldLabel = { display: 'grid', gap: 5, color: '#94a3b8', fontSize: 12, fontWeight: 700 };
const field = { background: '#0f172a', border: '1px solid #334155', color: '#f8fafc', borderRadius: 7, padding: '10px 9px', minWidth: 0 };
const sectionGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(290px, 1fr))', gap: 14, alignItems: 'start' };
const panel = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 14, minWidth: 0 };
const sectionHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 12 };
const sectionTitle = { color: '#f8fafc', fontSize: 21, margin: 0, lineHeight: 1.2 };
const smallTitle = { color: '#f8fafc', fontSize: 15, margin: '0 0 12px' };
const countBadge = { color: '#38bdf8', border: '1px solid #155e75', background: '#083344', borderRadius: 999, padding: '3px 9px', fontSize: 12, fontWeight: 800 };
const documentGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 };
const compactGrid = { display: 'grid', gap: 10 };
const docCard = { textAlign: 'left', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, padding: 13, cursor: 'pointer', display: 'grid', gap: 10, minWidth: 0 };
const docTop = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 };
const docIconBase = { borderRadius: 7, width: 44, height: 34, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 11 };
const pill = { border: '1px solid', borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 800 };
const docTitle = { color: '#f8fafc', fontSize: 15, fontWeight: 900, overflowWrap: 'anywhere' };
const docMeta = { color: '#94a3b8', fontSize: 12, overflowWrap: 'anywhere' };
const docInfoGrid = { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 7 };
const tagRow = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const statusRow = { display: 'flex', flexWrap: 'wrap', gap: 6 };
const tagPill = { color: '#cbd5e1', background: '#1e293b', border: '1px solid #334155', borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 700 };
const favorite = { color: '#fde68a', background: '#422006', border: '1px solid #92400e', borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 800 };
const infoBox = { display: 'grid', gap: 3, background: '#1e293b', border: '1px solid #334155', borderRadius: 7, padding: 9, minWidth: 0 };
const infoLabel = { color: '#64748b', fontSize: 11, fontWeight: 800, textTransform: 'uppercase' };
const infoValue = { color: '#e5e7eb', fontSize: 13, overflowWrap: 'anywhere' };
const muted = { color: '#94a3b8', fontSize: 13 };
const emptyState = { color: '#94a3b8', background: '#0f172a', border: '1px dashed #334155', borderRadius: 8, padding: 14, fontSize: 13 };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.78)', zIndex: 80, display: 'flex', justifyContent: 'flex-end' };
const detailPanel = { width: 'min(720px, 100vw)', background: '#0f172a', borderLeft: '1px solid #334155', padding: 16, overflowY: 'auto', boxSizing: 'border-box' };
const detailHeader = { display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', marginBottom: 14 };
const detailSection = { background: '#1e293b', border: '1px solid #334155', borderRadius: 8, padding: 13 };
const detailGrid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 9 };
const detailActions = { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 };
const warningGrid = { display: 'grid', gap: 8 };
const warningCard = { background: '#2b1d08', border: '1px solid #92400e', color: '#fde68a', borderRadius: 7, padding: 10, fontSize: 12, fontWeight: 700 };
const previewFrame = { width: '100%', height: 440, border: '1px solid #334155', borderRadius: 7, background: '#020617' };
const previewImage = { width: '100%', maxHeight: 440, objectFit: 'contain', border: '1px solid #334155', borderRadius: 7, background: '#020617' };
const placeholder = { minHeight: 180, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', background: '#020617', border: '1px solid #334155', borderRadius: 7, padding: 16, textAlign: 'center' };
const ocrBox = { color: '#e5e7eb', background: '#020617', border: '1px solid #334155', borderRadius: 7, padding: 12, maxHeight: 280, overflow: 'auto', whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12 };
const notice = { color: '#bfdbfe', background: '#172554', border: '1px solid #1d4ed8', borderRadius: 7, padding: 10, margin: '0 0 10px', fontSize: 13 };
const timeline = { display: 'flex', alignItems: 'flex-start', gap: 10, padding: '8px 0', borderBottom: '1px solid #334155' };
const timelineDot = { width: 9, height: 9, borderRadius: 999, background: '#38bdf8', marginTop: 5, flex: '0 0 auto' };
const stateScreen = { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' };
const processingBar = { display: 'flex', flexWrap: 'wrap', gap: 10 };
const processingBadge = (color) => ({ display: 'flex', alignItems: 'center', gap: 8, background: color + '18', border: `1px solid ${color}55`, borderRadius: 8, padding: '10px 14px', color, fontSize: 13, fontWeight: 600, flex: '1 1 220px' });
const mark = { background: '#fde68a', color: '#111827', padding: '0 2px', borderRadius: 3 };

function queueLabel(status, prefix) {
  const suffix = {
    WARTEND: 'wartend',
    IN_BEARBEITUNG: 'bearb.',
    ERFOLGREICH: 'ok',
    FEHLGESCHLAGEN: 'fehler',
    RETRY: 'retry',
    ABBRUCH: 'abbruch',
  }[status] || 'offen';
  return `${prefix}: ${suffix}`;
}

function pipelineBadge(status) {
  const tone = { wird_verarbeitet: '#6366f1', abgeschlossen: '#22c55e', fehlgeschlagen: '#ef4444' }[status] || '#64748b';
  return { color: tone, background: `${tone}18`, border: `1px solid ${tone}55`, borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 800 };
}

function queueBadge(status) {
  const tone = {
    WARTEND: '#fbbf24',
    IN_BEARBEITUNG: '#38bdf8',
    ERFOLGREICH: '#34d399',
    FEHLGESCHLAGEN: '#f87171',
    RETRY: '#fb7185',
    ABBRUCH: '#94a3b8',
  }[status] || '#64748b';
  return { color: tone, background: `${tone}18`, border: `1px solid ${tone}55`, borderRadius: 999, padding: '4px 8px', fontSize: 11, fontWeight: 800 };
}
